import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth, setToken, clearToken, setStoredUser, getStoredUser } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [ready, setReady] = useState(false);

  const applyUser = useCallback((u) => {
    setUser(u);
    if (u) setStoredUser(u);
  }, []);

  // On load, restore the session from the httpOnly refresh cookie (silent refresh).
  useEffect(() => {
    (async () => {
      try {
        const data = await auth.refresh();
        setToken(data.token);
        const me = await auth.me();
        applyUser(me);
      } catch {
        clearToken();
        setUser(null);
      } finally {
        setReady(true);
      }
    })();
  }, [applyUser]);

  const login = useCallback(async (email, password, remember) => {
    const data = await auth.login(email, password, remember);
    setToken(data.token);
    const me = await auth.me();
    applyUser(me);
    return me;
  }, [applyUser]);

  const logout = useCallback(async () => {
    try { await auth.logout(); } catch { /* ignore */ }
    clearToken();
    setUser(null);
  }, []);

  const hasPerm = useCallback((perm) => {
    const perms = user?.permissions || [];
    return perms.includes("*") || perms.includes(perm);
  }, [user]);

  const hasAnyPerm = useCallback((permList) => {
    const perms = user?.permissions || [];
    return perms.includes("*") || permList.some((p) => perms.includes(p));
  }, [user]);

  const value = { user, ready, login, logout, hasPerm, hasAnyPerm, role: user?.role, refreshUser: async () => applyUser(await auth.me()) };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
